/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  CircularProgress,
  TablePagination,
  Chip,
  Stack,
  Tooltip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import * as XLSX from "xlsx";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  GetApp as DownloadIcon,
  CalendarToday as CalIcon,
  FilterAltOff as ClearIcon,
} from "@mui/icons-material";
import {
  useBusinessSummary,
  BusinessSummaryProvider,
} from "../../contexts/alfaroojfresh/BusinessSummaryContext";
import dbData from "../../assets/db.json";
import STORE_NAMES from "../../utils/STORE_NAMES_FF";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
// ─────────────────────────────────────────
// Theme  (matches PurchaseOrders light-mode)
// ─────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#f8fafc", paper: "#ffffff" },
    primary: { main: "#3753b3" },
    text: { primary: "#0f172a", secondary: "#64748b" },
  },
  typography: {},
});
// ─────────────────────────────────────────
// Animations
// ─────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
// ─────────────────────────────────────────
// Styled components  (same palette as PurchaseOrders)
// ─────────────────────────────────────────
const Root = styled("div")({
  minHeight: "100vh",
  background: "#f8fafc",
  padding: "40px 48px",
  animation: `${fadeUp} 500ms ease both`,
});
const TitleBlock = styled("div")({
  "& .eyebrow": {
    fontSize: "0.65rem",
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    color: "#3753b3",
    marginBottom: 6,
  },
  "& h1": {
    margin: 0,
    fontSize: "2rem",
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.03em",
    lineHeight: 1,
  },
  "& .sub": { fontSize: "0.75rem", color: "#94a3b8", marginTop: 6 },
});
const ActionBtn = styled(Button)({
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: 8,
  padding: "8px 16px",
  border: "1px solid #e2e8f0",
  color: "#64748b",
  background: "#ffffff",
  transition: "all 0.2s",
  "&:hover": {
    borderColor: "#3753b3",
    color: "#3753b3",
    background: "rgba(245,158,11,0.05)",
  },
});
const StatCard = styled("div")({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "14px 18px",
  minWidth: 110,
  "& .val": {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1,
  },
  "& .lbl": {
    fontSize: "0.62rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#94a3b8",
    marginTop: 4,
  },
});
const FilterBar = styled("div")({
  display: "flex",
  gap: 12,
  marginBottom: 20,
  alignItems: "center",
  flexWrap: "wrap",
});
const SectionBox = styled("div")({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: "24px 28px",
  marginBottom: 20,
  boxShadow: "0 2px 12px rgba(15,23,42,0.04)",
});
const SectionTitle = styled("div")({
  fontSize: "0.85rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 16,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "& .accent": { color: "#3753b3" },
});
const StyledTableContainer = styled(Paper)({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
  overflow: "hidden",
});
const tableHeadSx = {
  "& .MuiTableCell-head": {
    fontSize: "0.65rem",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: "#94a3b8",
    background: "#f8fafc",
    fontWeight: 600,
    padding: "14px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
};
const tableBodySx = {
  "& .MuiTableRow-root": {
    transition: "background 0.15s",
    "&:hover": { background: "#fefce8" },
  },
  "& .MuiTableCell-body": {
    fontSize: "0.78rem",
    color: "#475569",
    padding: "12px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
};
const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.78rem",
    background: "#ffffff",
    borderRadius: 2,
    color: "#0f172a",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#3753b3" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.75rem", color: "#94a3b8" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#3753b3" },
};
const menuPropsSx = {
  PaperProps: {
    sx: {
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: 2,
      boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
      "& .MuiMenuItem-root": {
        fontSize: "0.78rem",
        color: "#475569",
        "&:hover": { background: "#fefce8", color: "#d97706" },
        "&.Mui-selected": {
          background: "rgba(245,158,11,0.08)",
          color: "#3753b3",
        },
      },
    },
  },
};
const selectSx = {
  fontSize: "0.78rem",
  background: "#ffffff",
  borderRadius: 2,
  color: "#0f172a",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3753b3" },
};
const inputLabelSx = {
  fontSize: "0.75rem",
  color: "#94a3b8",
  "&.Mui-focused": { color: "#3753b3" },
};
// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────
const parseDynamicKey = (key) => {
  if (key.startsWith("sd_")) {
    let tag = "";
    if (key.includes("_t_")) tag = "Take Away";
    else if (key.includes("_d_")) tag = "Dine In";
    else if (key.includes("_w_")) tag = "Walk In";
    return { type: "Net Sales", tag, key };
  }
  if (key.startsWith("pt_")) return { type: "Payment Type", tag: key, key };
  if (key.startsWith("dd_")) return { type: "Order Discount", tag: key, key };
  if (key.startsWith("iv_")) return { type: "Item Discount", tag: key, key };
  if (key.startsWith("hv_")) return { type: "Tips", tag: key, key };
  return { type: "Other", tag: "", key };
};
const names = [
  "Al Farooj Fresh - Abu Hail Dubai",
  "Al Farooj Fresh - Warehouse",
  "Al Farooj Fresh - Central Kitchen",
  "Al Farooj Fresh - Al Barsha Mall Dubai",
  "Al Farooj Fresh - King Faisal Sharjah",
  "Al Farooj Fresh - Manaseer Al Ain",
  "Al Farooj Fresh - Tawam Al Ain",
  "AL Farooj Fresh - Mirdiff Way Food Truck",
  "Al Farooj Fresh - LIWA",
  "Al Farooj Fresh - Catering ( Al Wasl Club )",
  "Al Farooj Fresh - Ajman",
  "Al Farooj Fresh - Kiosk",
  "Al Farooj Fresh - Al Hilli Al Ain",
  "Al Farooj Fresh - Ajman Food Truck",
];
const NUMERIC_STORE_NAMES = {
  1: "Al Farooj Fresh - Abu Hail",
  2: "Warehouse",
  3: "Central Kitchen",
  4: "Al Farooj Fresh - Al Barsha Mall",
  5: "Al Farooj Fresh - Sharjah",
  6: "Al Farooj Fresh - Manaseer Al Ain",
  7: "Al Farooj Fresh - Tawam Al Ain",
  8: "AL Farooj Fresh - Truck Mirdiff Way",
  9: "Al Farooj Fresh - Zayed Festival AD",
  10: "Al Farooj Fresh - Catering ( Al Wasl Club )",
  11: "Al Farooj Fresh - Ajman",
  12: "Al Farooj Fresh - Kiosk",
  13: "Al Farooj Fresh-Al Hilli Al Ain",
  14: "Al Farooj Fresh - Ajman Food Truck",
};
const extractStoreKeyFromKey = (key) => {
  const parts = key.split("_");
  const numeric = parts.find((p) => /^\d+$/.test(p));
  if (numeric) return numeric;
  return null;
};
const mapStoreKeyToName = (key) => {
  if (!key) return "Unknown Store";
  if (NUMERIC_STORE_NAMES[key]) return NUMERIC_STORE_NAMES[key];
  if (STORE_NAMES[key]) return STORE_NAMES[key];
  return `Store ${key}`;
};
// ─────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────
const BusinessSummary = () => {
  const {
    businessSummary,
    loading,
    error,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
  } = useBusinessSummary();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  // Advanced search states
  const [netSalesMin, setNetSalesMin] = React.useState("");
  const [netSalesMax, setNetSalesMax] = React.useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = React.useState("");
  const [storeFilter, setStoreFilter] = React.useState("");
  // Payment types table states
  const [pagePT, setPagePT] = React.useState(0);
  const [rowsPerPagePT, setRowsPerPagePT] = React.useState(10);
  const [paymentNameFilter, setPaymentNameFilter] = React.useState("");
  const [dateFromPT, setDateFromPT] = React.useState("");
  const [dateToPT, setDateToPT] = React.useState("");
  const normalizeControlDates = (control = []) =>
    control
      .map((raw) => {
        const clean = raw.replace(/^Hours:\s*/, "");
        const d = new Date(clean);
        return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
      })
      .filter(Boolean);
  // Has any filter active?
  const hasMainFilters = dateFrom || dateTo || netSalesMin || netSalesMax || paymentTypeFilter || storeFilter;
  const hasPTFilters = dateFromPT || dateToPT || paymentNameFilter;
  // ── dynamic columns ──
  const dynamicColumns = useMemo(() => {
    if (!businessSummary) return [];
    const keys = new Set();
    businessSummary.forEach((item) => {
      (item.data || []).forEach((d) =>
        Object.keys(d).forEach((k) => {
          if (
            k.startsWith("sd_") ||
            k.startsWith("pt_") ||
            k.startsWith("dd_") ||
            k.startsWith("hv_") ||
            k.startsWith("iv_")
          )
            keys.add(k);
        })
      );
    });
    return Array.from(keys).map(parseDynamicKey);
  }, [businessSummary]);
  // ── format data ──
  const formattedData = useMemo(() => {
    if (!businessSummary) return [];
    const rows = [];
    businessSummary.forEach((item) => {
      const dataArray = item.data || [];
      const controlDates = normalizeControlDates(
        item?.control_structure?.added || []
      );
      dataArray.forEach((d, index) => {
        const groupedAll = {
          "Net Sales": 0,
          Tax: 0,
          "Payment Type": {},
          "Order Discount": 0,
          "Item Discount": 0,
          Tips: 0,
        };
        const perStore = {};
        dynamicColumns.forEach((col) => {
          if (d[col.key] == null) return;
          const value = d[col.key];
          const storeKey = extractStoreKeyFromKey(col.key);
          const ensureStore = (k) => {
            if (!perStore[k])
              perStore[k] = {
                "Net Sales": 0,
                Tax: 0,
                "Payment Type": {},
                "Order Discount": 0,
                "Item Discount": 0,
                Tips: 0,
              };
            return perStore[k];
          };
          if (col.type === "Payment Type") {
            const paymentId = col.key.split("_").pop();
            const match = dbData.find((db) => db._id === paymentId);
            const name = match ? match.name : paymentId;
            if (storeKey) {
              const s = ensureStore(storeKey);
              s["Payment Type"][name] = (s["Payment Type"][name] || 0) + value;
            } else {
              groupedAll["Payment Type"][name] =
                (groupedAll["Payment Type"][name] || 0) + value;
            }
          } else if (col.type === "Net Sales") {
            if (storeKey) {
              const s = ensureStore(storeKey);
              s["Net Sales"] += value;
            } else groupedAll["Net Sales"] += value;
          } else if (col.type === "Order Discount") {
            if (storeKey) {
              const s = ensureStore(storeKey);
              s["Order Discount"] += value;
            } else groupedAll["Order Discount"] += value;
          } else if (col.type === "Item Discount") {
            if (storeKey) {
              const s = ensureStore(storeKey);
              s["Item Discount"] += value;
            } else groupedAll["Item Discount"] += value;
          } else if (col.type === "Tips") {
            if (storeKey) {
              const s = ensureStore(storeKey);
              s["Tips"] += value;
            } else groupedAll["Tips"] += value;
          }
        });
        Object.values(perStore).forEach((s) => {
          s["Tax"] = Math.round(s["Net Sales"] * 0.05);
        });
        Object.values(perStore).forEach((s) => {
          groupedAll["Net Sales"] += s["Net Sales"] || 0;
          groupedAll["Tax"] += s["Tax"] || 0;
          groupedAll["Order Discount"] += s["Order Discount"] || 0;
          groupedAll["Item Discount"] += s["Item Discount"] || 0;
          groupedAll["Tips"] += s["Tips"] || 0;
          Object.entries(s["Payment Type"] || {}).forEach(([n, v]) => {
            groupedAll["Payment Type"][n] =
              (groupedAll["Payment Type"][n] || 0) + v;
          });
        });
        groupedAll["Tax"] = Math.round(groupedAll["Net Sales"] * 0.05);
        const addedDate = controlDates[index] ?? null;
        const storeIds = Object.keys(perStore);
        const row = {
          addedDate,
          addedDateTs: addedDate ? new Date(addedDate).getTime() : null,
          iv: d.iv ?? "N/A",
          sd: d.sd ?? "N/A",
          hv: d.hv ?? "N/A",
          dd: d.dd ?? "N/A",
          storeIds,
          perStore,
          grouped: { ...groupedAll },
        };
        rows.push(row);
      });
    });
    return rows;
  }, [businessSummary, dynamicColumns]);
  // ── unique payment types ──
  const uniquePaymentTypes = useMemo(() => {
    const types = new Set();
    formattedData.forEach((row) => {
      const pt = row.grouped["Payment Type"];
      if (pt) {
        Object.keys(pt).forEach((name) => types.add(name));
      }
      Object.values(row.perStore || {}).forEach((s) => {
        Object.keys(s["Payment Type"] || {}).forEach((name) => types.add(name));
      });
    });
    return Array.from(types);
  }, [formattedData]);
  // ── unique stores ──
  const uniqueStores = useMemo(() => {
    const stores = new Set();
    formattedData.forEach((row) => {
      Object.keys(row.perStore || {}).forEach((k) => stores.add(k));
    });
    return Array.from(stores).map((id) => ({
      id,
      name: mapStoreKeyToName(id),
    }));
  }, [formattedData]);
  // ── filtered data ──
  const filteredData = useMemo(() => {
    const emptyGrouped = () => ({
      "Net Sales": 0,
      "Payment Type": {},
      "Order Discount": 0,
      "Item Discount": 0,
      Tips: 0,
    });
    return formattedData
      .filter((row) => row.addedDate !== "N/A")
      .map((row) => {
        if (storeFilter) {
          const per =
            row.perStore && row.perStore[storeFilter]
              ? row.perStore[storeFilter]
              : emptyGrouped();
          return { ...row, grouped: per };
        }
        return row;
      })
      .filter((row) => {
        if (!row.addedDateTs) return false;
        const fromTs = dateFrom
          ? new Date(dateFrom + "T00:00:00").getTime()
          : null;
        const toTs = dateTo ? new Date(dateTo + "T23:59:59").getTime() : null;
        if (fromTs && row.addedDateTs < fromTs) return false;
        if (toTs && row.addedDateTs > toTs) return false;
        return true;
      });
  }, [formattedData, dateFrom, dateTo, storeFilter]);
  // ── calculate totals ──
  const totals = useMemo(() => {
    let totalNetSales = 0;
    let totalTax = 0;
    let totalOrderDiscount = 0;
    let totalItemDiscount = 0;
    let totalTips = 0;
    filteredData.forEach((row) => {
      totalNetSales += row.grouped["Net Sales"] || 0;
      totalTax += row.grouped["Tax"] || 0;
      totalOrderDiscount += row.grouped["Order Discount"] || 0;
      totalItemDiscount += row.grouped["Item Discount"] || 0;
      totalTips += row.grouped["Tips"] || 0;
    });
    return {
      totalNetSales,
      totalTax,
      totalOrderDiscount,
      totalItemDiscount,
      totalTips,
    };
  }, [filteredData]);
  // ── payment type data ──
  const paymentTypeData = useMemo(() => {
    const rows = [];
    filteredData.forEach((row) => {
      const pt = row.grouped["Payment Type"];
      if (pt) {
        Object.entries(pt).forEach(([name, value]) => {
          rows.push({
            addedDate: row.addedDate,
            paymentName: name,
            value: value,
          });
        });
      }
    });
    return rows;
  }, [filteredData]);
  // ── filtered payment type data ──
  const filteredPaymentTypeData = useMemo(() => {
    return paymentTypeData.filter((row) => {
      const rowDate = new Date(row.addedDate);
      const fromDate = dateFromPT ? new Date(dateFromPT + "T00:00:00") : null;
      const toDate = dateToPT ? new Date(dateToPT + "T23:59:59") : null;
      if (fromDate && rowDate < fromDate) return false;
      if (toDate && rowDate > toDate) return false;
      if (paymentNameFilter && row.paymentName !== paymentNameFilter)
        return false;
      return true;
    });
  }, [paymentTypeData, dateFromPT, dateToPT, paymentNameFilter]);
  // ── payment type totals ──
  const totalsPT = useMemo(() => {
    const totals = {};
    filteredPaymentTypeData.forEach((row) => {
      if (!totals[row.paymentName]) totals[row.paymentName] = 0;
      totals[row.paymentName] += row.value;
    });
    return totals;
  }, [filteredPaymentTypeData]);
  const columns = [
    { header: "Added Date", key: "addedDate" },
    { header: "Net Sales (AED)", key: "Net Sales" },
    { header: "Tax (5%) (AED)", key: "Tax" },
    { header: "Payment Type", key: "Payment Type" },
    { header: "Order Discount (AED)", key: "Order Discount" },
    { header: "Item Discount (AED)", key: "Item Discount" },
    { header: "Tips (AED)", key: "Tips" },
  ];
  const pageRows = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const pageRowsPT = filteredPaymentTypeData.slice(
    pagePT * rowsPerPagePT,
    (pagePT + 1) * rowsPerPagePT
  );
  const formatMoneyFromCents = (cents) => {
    if (cents == null) return "0,00";
    return (cents / 100).toFixed(2).replace(".", ",");
  };
  const applyTaxToCents = (cents, taxPercent = 5) => {
    return Math.round(cents * (1 + taxPercent / 100));
  };
  const paymentTypeString = (paymentObj = {}, applyTax = false) => {
    return Object.entries(paymentObj)
      .map(([name, cents]) => {
        const value = applyTax ? applyTaxToCents(cents) : cents;
        return `${formatMoneyFromCents(value)} (${name})`;
      })
      .join(", ");
  };
  // ── export to excel ──
  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Business Summary");
    const allPaymentTypes = Array.from(
      new Set(
        filteredData.flatMap((r) =>
          Object.keys(r.grouped["Payment Type"] || {})
        )
      )
    );
    const netSubHeaders = [
      "Added Date",
      "Net Sales (AED)",
      ...allPaymentTypes.map((p) => `${p} (AED)`),
      "Order Discount (AED)",
      "Item Discount (AED)",
      "Tips (AED)",
    ];
    const grossSubHeaders = [
      "Added Date",
      "Gross Sales (AED)",
      "VAT (AED)",
      ...allPaymentTypes.map((p) => `${p} (AED)`),
      "Order Discount (AED)",
      "Item Discount (AED)",
      "Tips (AED)",
    ];
    const netColsCount = netSubHeaders.length;
    const grossColsCount = grossSubHeaders.length;
    const totalCols = netColsCount + grossColsCount;
    const headerRow1 = Array(totalCols).fill("");
    headerRow1[0] = "Net Sales";
    headerRow1[netColsCount] = "Gross Sales (+5%)";
    ws.addRow(headerRow1);
    ws.mergeCells(1, 1, 1, netColsCount);
    ws.mergeCells(1, netColsCount + 1, 1, totalCols);
    const r1 = ws.getRow(1);
    r1.height = 30;
    r1.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4F81BD" },
      };
      cell.font = { color: { argb: "FFFFFF" }, bold: true, size: 16 };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });
    ws.addRow([...netSubHeaders, ...grossSubHeaders]);
    const r2 = ws.getRow(2);
    r2.height = 22;
    r2.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "DDEBF7" },
      };
      cell.font = { bold: true, size: 12 };
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
    });
    filteredData.forEach((row) => {
      const netSales = row.grouped["Net Sales"] || 0;
      const vat = Math.round(netSales * 0.05);
      const grossSales = netSales + vat;
      const netOrderDiscount = row.grouped["Order Discount"] || 0;
      const netItemDiscount = row.grouped["Item Discount"] || 0;
      const netTips = row.grouped["Tips"] || 0;
      const netPtValues = allPaymentTypes.map(
        (pt) => (row.grouped["Payment Type"]?.[pt] || 0) / 100
      );
      const grossPtValues = netPtValues.slice();
      const dataRow = [
        row.addedDate,
        netSales / 100,
        ...netPtValues,
        netOrderDiscount / 100,
        netItemDiscount / 100,
        netTips / 100,
        row.addedDate,
        grossSales / 100,
        vat / 100,
        ...grossPtValues,
        netOrderDiscount / 100,
        netItemDiscount / 100,
        netTips / 100,
      ];
      const added = ws.addRow(dataRow);
      added.eachCell((cell, colNumber) => {
        if (
          colNumber !== 1 &&
          colNumber !== netColsCount + 1 &&
          typeof cell.value === "number"
        ) {
          cell.numFmt = "#,##0.00";
        }
      });
    });
    ws.addRow([]);
    const paymentTypeTotalsNet = allPaymentTypes.map((pt) =>
      filteredData.reduce(
        (s, r) => s + (r.grouped["Payment Type"]?.[pt] || 0),
        0
      )
    );
    const totalNetSales = totals.totalNetSales || 0;
    const totalVat = Math.round(totalNetSales * 0.05);
    const totalGrossSales = totalNetSales + totalVat;
    const totalOrderDiscount = totals.totalOrderDiscount || 0;
    const totalItemDiscount = totals.totalItemDiscount || 0;
    const totalTips = totals.totalTips || 0;
    const totalsRowValues = [
      "Total",
      totalNetSales / 100,
      ...paymentTypeTotalsNet.map((c) => c / 100),
      totalOrderDiscount / 100,
      totalItemDiscount / 100,
      totalTips / 100,
      "Total Gross",
      totalGrossSales / 100,
      totalVat / 100,
      ...paymentTypeTotalsNet.map((c) => c / 100),
      totalOrderDiscount / 100,
      totalItemDiscount / 100,
      totalTips / 100,
    ];
    const totalsRow = ws.addRow(totalsRowValues);
    totalsRow.eachCell((cell, colNumber) => {
      if (typeof cell.value === "number") cell.numFmt = "#,##0.00";
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F2F2F2" },
      };
    });
    const leftWidths = [14, 15, ...allPaymentTypes.map(() => 16), 18, 18, 14];
    const rightWidths = [
      14,
      15,
      12,
      ...allPaymentTypes.map(() => 16),
      18,
      18,
      14,
    ];
    const finalWidths = [...leftWidths, ...rightWidths];
    finalWidths.forEach((w, i) => (ws.getColumn(i + 1).width = w));
    const buf = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buf]), "business_summary.xlsx");
  };
  // ── Loading / Error states ──
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ background: "#f8fafc" }}
      >
        <CircularProgress sx={{ color: "#3753b3" }} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ background: "#f8fafc" }}
      >
        <Typography sx={{ color: "#ef4444" }}>Error: {error}</Typography>
      </Box>
    );
  }
  if (!formattedData.length) {
    return (
      <ThemeProvider theme={theme}>
        <Root>
          <TitleBlock>
            <div className="eyebrow">Finance</div>
            <h1>Business Summary</h1>
          </TitleBlock>
          <SectionBox>
            <Typography
              sx={{
                color: "#94a3b8",
                textAlign: "center",
                padding: "36px",
                fontStyle: "italic",
              }}
            >
              No business summary data available.
            </Typography>
          </SectionBox>
        </Root>
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <Root>
        {/* ── Page header ── */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <TitleBlock>
            <div className="eyebrow">Finance</div>
            <h1>Business Summary</h1>
            <div className="sub">
              Overview of business data with detailed fields.
            </div>
          </TitleBlock>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <StatCard>
              <div className="val">
                {formatMoneyFromCents(totals.totalNetSales)}
              </div>
              <div className="lbl">Net Sales (AED)</div>
            </StatCard>
            <StatCard>
              <div className="val">
                {formatMoneyFromCents(totals.totalOrderDiscount)}
              </div>
              <div className="lbl">Order Discount</div>
            </StatCard>
            <StatCard>
              <div className="val">
                {formatMoneyFromCents(totals.totalItemDiscount)}
              </div>
              <div className="lbl">Item Discount</div>
            </StatCard>
            <StatCard>
              <div className="val">
                {formatMoneyFromCents(totals.totalTips)}
              </div>
              <div className="lbl">Tips (AED)</div>
            </StatCard>
          </Box>
        </Box>
        {/* ── Filters & Export ── */}
        <SectionBox>
          <SectionTitle>
            <span className="accent">◈</span> Advanced Search & Export
          </SectionTitle>
          <FilterBar>
            <TextField
              label="Date From"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalIcon sx={{ color: "#94a3b8", fontSize: 14 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, minWidth: 160 }}
            />
            <TextField
              label="Date To"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalIcon sx={{ color: "#94a3b8", fontSize: 14 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, minWidth: 160 }}
            />
            <TextField
              label="Min Net Sales"
              type="number"
              value={netSalesMin}
              onChange={(e) => setNetSalesMin(e.target.value)}
              size="small"
              sx={{ ...inputSx, minWidth: 140 }}
            />
            <TextField
              label="Max Net Sales"
              type="number"
              value={netSalesMax}
              onChange={(e) => setNetSalesMax(e.target.value)}
              size="small"
              sx={{ ...inputSx, minWidth: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={inputLabelSx}>Payment Type</InputLabel>
              <Select
                value={paymentTypeFilter}
                onChange={(e) => setPaymentTypeFilter(e.target.value)}
                label="Payment Type"
                sx={selectSx}
                MenuProps={menuPropsSx}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {uniquePaymentTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel sx={inputLabelSx}>Store</InputLabel>
              <Select
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
                label="Store"
                sx={selectSx}
                MenuProps={menuPropsSx}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {uniqueStores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ActionBtn
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
            >
              Excel
            </ActionBtn>
            {hasMainFilters && (
              <ActionBtn
                startIcon={<ClearIcon sx={{ fontSize: 15 }} />}
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                  setNetSalesMin("");
                  setNetSalesMax("");
                  setPaymentTypeFilter("");
                  setStoreFilter("");
                }}
                sx={{
                  borderColor: "#fecaca",
                  color: "#ef4444",
                  "&:hover": {
                    borderColor: "#ef4444",
                    background: "rgba(239,68,68,0.05)",
                  },
                }}
              >
                Clear
              </ActionBtn>
            )}
          </FilterBar>
        </SectionBox>
        {/* ── Main Table ── */}
        <SectionBox>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
            flexWrap="wrap"
            gap={1}
          >
            <SectionTitle sx={{ mb: 0 }}>
              <span className="accent">◈</span> Business Data
              <span
                style={{
                  color: "#94a3b8",
                  fontWeight: 400,
                  fontSize: "0.72rem",
                }}
              >
                — {filteredData.length} records
              </span>
            </SectionTitle>
          </Box>
          <TableContainer component={StyledTableContainer}>
            <Table sx={{ ...tableHeadSx, ...tableBodySx }}>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.key}>{col.header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((row, idx) => (
                  <TableRow key={idx}>
                    {columns.map((col) => {
                      const grouped = row.grouped && row.grouped[col.key];
                      if (grouped) {
                        return (
                          <TableCell
                            key={col.key}
                            sx={{ verticalAlign: "top" }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                            >
                              {col.key === "Payment Type" ? (
                                Object.entries(grouped).map(
                                  ([name, sum], i) => (
                                    <Tooltip
                                      key={i}
                                      style={{ marginTop: "5px" }}
                                      title={`Sum: ${(sum / 100)
                                        .toFixed(2)
                                        .replace(".", ",")}`}
                                    >
                                      <Chip
                                        label={`${(sum / 100)
                                          .toFixed(2)
                                          .replace(".", ",")} (${name})`}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          marginBottom: 1,
                                          borderColor: "rgba(55,83,179,0.3)",
                                          color: "#3753b3",
                                          fontSize: "0.72rem",
                                        }}
                                      />
                                    </Tooltip>
                                  )
                                )
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.78rem",
                                    color:
                                      col.key === "Net Sales" ||
                                      col.key === "Tax"
                                        ? "#3753b3"
                                        : col.key === "Order Discount"
                                        ? "#d97706"
                                        : "#0f172a",
                                  }}
                                >
                                  {(grouped / 100).toFixed(2).replace(".", ",")}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                        );
                      } else {
                        return (
                          <TableCell key={col.key}>{row[col.key]}</TableCell>
                        );
                      }
                    })}
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ background: "#f8fafc" }}>
                  <TableCell
                    colSpan={1}
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.75rem !important",
                    }}
                  >
                    Total
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#3753b3 !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {(totals.totalNetSales / 100)
                      .toFixed(2)
                      .replace(".", ",") || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#3753b3 !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {(totals.totalTax / 100).toFixed(2).replace(".", ",")}
                  </TableCell>
                  <TableCell />
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#d97706 !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {(totals.totalOrderDiscount / 100)
                      .toFixed(2)
                      .replace(".", ",")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {(totals.totalItemDiscount / 100)
                      .toFixed(2)
                      .replace(".", ",")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {(totals.totalTips / 100).toFixed(2).replace(".", ",")}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ background: "#f8fafc" }}>
                  <TableCell
                    colSpan={1}
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.75rem !important",
                    }}
                  >
                    Total with taxes (5%)
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#3753b3 !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {((totals.totalNetSales / 100) * 1.05)
                      .toFixed(3)
                      .replace(".", ",")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#3753b3 !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {(totals.totalTax / 100).toFixed(2).replace(".", ",")}
                  </TableCell>
                  <TableCell />
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#d97706 !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {(totals.totalOrderDiscount / 100)
                      .toFixed(2)
                      .replace(".", ",")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {(totals.totalItemDiscount / 100)
                      .toFixed(2)
                      .replace(".", ",")}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {(totals.totalTips / 100).toFixed(2).replace(".", ",")}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredData.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              color: "#94a3b8",
              fontSize: "0.72rem",
              "& .MuiSelect-icon": { color: "#94a3b8" },
              "& .MuiIconButton-root": {
                color: "#94a3b8",
                "&:hover": { color: "#3753b3" },
              },
              "& .Mui-disabled": { color: "#e2e8f0 !important" },
            }}
          />
        </SectionBox>
        {/* ── Payment Types Details ── */}
        <SectionBox>
          <SectionTitle>
            <span className="accent">◈</span> Payment Types Details
          </SectionTitle>
          <FilterBar>
            <TextField
              label="Date From"
              type="date"
              value={dateFromPT}
              onChange={(e) => setDateFromPT(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalIcon sx={{ color: "#94a3b8", fontSize: 14 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, minWidth: 160 }}
            />
            <TextField
              label="Date To"
              type="date"
              value={dateToPT}
              onChange={(e) => setDateToPT(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalIcon sx={{ color: "#94a3b8", fontSize: 14 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, minWidth: 160 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel sx={inputLabelSx}>Payment Name</InputLabel>
              <Select
                value={paymentNameFilter}
                onChange={(e) => setPaymentNameFilter(e.target.value)}
                label="Payment Name"
                sx={selectSx}
                MenuProps={menuPropsSx}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {uniquePaymentTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {hasPTFilters && (
              <ActionBtn
                startIcon={<ClearIcon sx={{ fontSize: 15 }} />}
                onClick={() => {
                  setDateFromPT("");
                  setDateToPT("");
                  setPaymentNameFilter("");
                }}
                sx={{
                  borderColor: "#fecaca",
                  color: "#ef4444",
                  "&:hover": {
                    borderColor: "#ef4444",
                    background: "rgba(239,68,68,0.05)",
                  },
                }}
              >
                Clear
              </ActionBtn>
            )}
          </FilterBar>
          <TableContainer component={StyledTableContainer}>
            <Table sx={{ ...tableHeadSx, ...tableBodySx }}>
              <TableHead>
                <TableRow>
                  <TableCell>Added Date</TableCell>
                  <TableCell>Payment Name</TableCell>
                  <TableCell align="right">Value (AED)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRowsPT.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.addedDate ?? "—"}</TableCell>
                    <TableCell>
                      <span style={{ color: "#0f172a", fontWeight: 600 }}>
                        {row.paymentName || "—"}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={{ color: "#3753b3", fontWeight: 600 }}>
                        {(row.value / 100).toFixed(2).replace(".", ",") || "—"}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                {Object.entries(totalsPT).map(([name, total]) => (
                  <TableRow key={name} sx={{ background: "#f8fafc" }}>
                    <TableCell
                      colSpan={2}
                      sx={{
                        fontWeight: 700,
                        color: "#0f172a !important",
                        fontSize: "0.75rem !important",
                      }}
                    >
                      Total {name}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 700,
                        color: "#3753b3 !important",
                        fontSize: "0.78rem !important",
                      }}
                    >
                      {(total / 100).toFixed(2).replace(".", ",")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableFooter>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredPaymentTypeData.length}
            page={pagePT}
            rowsPerPage={rowsPerPagePT}
            onPageChange={(e, newPage) => setPagePT(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPagePT(parseInt(e.target.value, 10));
              setPagePT(0);
            }}
            sx={{
              color: "#94a3b8",
              fontSize: "0.72rem",
              "& .MuiSelect-icon": { color: "#94a3b8" },
              "& .MuiIconButton-root": {
                color: "#94a3b8",
                "&:hover": { color: "#3753b3" },
              },
              "& .Mui-disabled": { color: "#e2e8f0 !important" },
            }}
          />
        </SectionBox>
      </Root>
    </ThemeProvider>
  );
};
// ── Wrapper ──
const BusinessSummaryPage = () => (
  <BusinessSummaryProvider>
    <BusinessSummary />
  </BusinessSummaryProvider>
);
export default BusinessSummaryPage;